import React, { useState, useEffect, useMemo, useCallback } from "react";
import Styles from "./EditTermsConnection.module.css";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { formatDate, formatSlotTime } from "../../../../helpers/dateFormat";
// Removed unused imports: TimeRangePicker, DateRangePicker, addDays, format, rdrLocales, ReactDOM
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import DateInput from "../../../../commons/Inputs/DateInputBailateral/DateInput";
import TimeInput from "../../../../commons/Inputs/TimeInputBailateral/TimeInput";

// --- Custom Hooks and Helpers (Separation of Concerns) ---

/**
 * Hook to process raw data, map selfSlot IDs, and manage the selected requisition.
 * @param {Array} wholeData - The full data array for mapping indices.
 * @param {Array} data - The current filtered data array.
 * @returns {Object} Processed state: { dataWithIndex, uniqueSelfReq, selectedSelfReq, setSelectedSelfReq }
 */
const useTermsDataProcessing = (wholeData, data) => {
  const [dataWithIndex, setDataWithIndex] = useState([]);
  const [uniqueSelfReq, setUniqueSelfReq] = useState([]);
  const [selectedSelfReq, setSelectedSelfReq] = useState(null);

  useEffect(() => {
    // 1. Build mapping from selfSlot.id to its user-friendly index (Req 1, Req 2, etc.)
    const idToIndexMap = {};
    if (wholeData && Array.isArray(wholeData)) {
      wholeData.forEach((item) => {
        item.matches?.forEach((match, idx) => {
          idToIndexMap[match.selfSlot.id] = idx + 1;
        });
      });
    }

    // 2. Helper to apply mapping. 'arr' must be an array.
    const addIndexToMatches = (arr) =>
      arr?.map((item) => ({
        ...item,
        matches:
          item.matches?.map((match) => ({
            ...match,
            selfSlot: {
              ...match.selfSlot,
              index: idToIndexMap[match.selfSlot.id] || 1,
            },
          })) ?? [],
      }));

    if (data) {
      // FIX: Ensure the single data object is contained within an array
      const dataAsArray = Array.isArray(data) ? data : [data];

      const processedData = addIndexToMatches(dataAsArray);
      setDataWithIndex(processedData);

      // 3. Extract unique requisitions
      const allSelfSlots =
        processedData[0]?.matches?.map((match) => ({
          id: match.selfSlot.id,
          reqId: match.selfSlot.index,
          matchIndex: match.selfSlot.id,
        })) || [];

      const unique = allSelfSlots.filter(
        (item, idx, arr) =>
          arr.findIndex((i) => i.matchIndex === item.matchIndex) === idx,
      );
      setUniqueSelfReq(unique);

      // 4. Set initial selection or update
      if (
        !selectedSelfReq ||
        !unique.some((u) => u.matchIndex === selectedSelfReq.matchIndex)
      ) {
        setSelectedSelfReq(unique[0]);
      } else {
        setSelectedSelfReq(
          unique.find((u) => u.matchIndex === selectedSelfReq.matchIndex),
        );
      }
    }
  }, [wholeData, data]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    dataWithIndex,
    uniqueSelfReq,
    selectedSelfReq,
    setSelectedSelfReq,
  };
};

/**
 * Helper to determine default values for connection fields (Rate, Quantity, Dates, Times).
 * This logic handles whether the user is the initiator or the counterparty.
 */
const getDefaultValues = (
  user,
  selfSlot,
  matchSlots = [],
  counterparty = null,
  capacityData,
) => {
  const isDisabled =
    (user === "consumer" && counterparty?.initiatorType === "generator") ||
    (user === "generator" && counterparty?.initiatorType === "consumer");

  const defaults = {
    paymentSecurity:
      counterparty?.finalizedPaymentDetails?.paymentSecurity ??
      capacityData.paymentSecurity ??
      "",
    initialMargin:
      counterparty?.finalizedPaymentDetails?.initialMargin ??
      capacityData.initialMargin ??
      "",
    emd: counterparty?.finalizedPaymentDetails?.emd ?? capacityData.emd ?? "",
  };

  matchSlots.forEach((matchSlot, i) => {
    const idx = i + 1;
    const finalizedValues = matchSlot?.finalizedConnectionValues;

    // Use specific source keys based on user role and initiator status
    const rateKey = user === "generator" ? "sellingCost" : "landingCost";
    const energyKey = user === "generator" ? "sellEnergy" : "purchaseEnergy";

    if (!isDisabled) {
      // User is initiator (use selfSlot for defaults)
      defaults[`req${idx}_energyRate`] =
        finalizedValues?.amount ??
        matchSlot?.energyRateStr ??
        selfSlot?.[rateKey] ??
        "";
      defaults[`req${idx}_quantity`] =
        finalizedValues?.quantity ?? selfSlot?.[energyKey] ?? "";
      defaults[`req${idx}_startDate`] =
        finalizedValues?.dateStartAt ?? selfSlot?.dateStartAt ?? "";
      defaults[`req${idx}_endDate`] =
        finalizedValues?.dateEndAt ?? selfSlot?.dateEndAt ?? "";
      defaults[`req${idx}_startTime`] =
        finalizedValues?.slotStartAt ?? selfSlot?.slotStartAt ?? "";
      defaults[`req${idx}_endTime`] =
        finalizedValues?.slotEndAt ?? selfSlot?.slotEndAt ?? "";
    } else {
      // Counterparty is initiator (use matchSlot/finalized values)
      defaults[`req${idx}_energyRate`] =
        finalizedValues?.amount ??
        matchSlot?.energyRateStr ??
        matchSlot?.[rateKey] ??
        "";
      defaults[`req${idx}_quantity`] =
        finalizedValues?.quantity ?? matchSlot?.[energyKey] ?? "";
      defaults[`req${idx}_startDate`] =
        finalizedValues?.dateStartAt ?? matchSlot?.dateStartAt ?? "";
      defaults[`req${idx}_endDate`] =
        finalizedValues?.dateEndAt ?? matchSlot?.dateEndAt ?? "";
      defaults[`req${idx}_startTime`] =
        finalizedValues?.slotStartAt ?? matchSlot?.slotStartAt ?? "";
      defaults[`req${idx}_endTime`] =
        finalizedValues?.slotEndAt ?? matchSlot?.slotEndAt ?? "";
    }
  });
  return defaults;
};

// --- Main Component ---

const EditTermsConnection = ({
  open,
  handleClose,
  data,
  handleTermsSubmit,
  wholeData,
  termsSubmitLoading,
}) => {
  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole,
  )?.toLowerCase();
  const isConsumer = user === "consumer";
  const isGenerator = user === "generator";

  // State for manual overrides
  const [finalValues, setFinalValues] = useState({});
  const [sharedPaymentValues, setSharedPaymentValues] = useState({});
  const [conformOpen, setConformOpen] = useState(false);

  // Use custom hook for data preparation and requisition navigation
  const { dataWithIndex, uniqueSelfReq, selectedSelfReq, setSelectedSelfReq } =
    useTermsDataProcessing(wholeData, data);

  // --- Utility Functions (Memoized) ---

  const getCapacityData = useCallback(() => {
    if (isConsumer) return dataWithIndex[0]?.capacityConsumer || {};
    if (isGenerator) return dataWithIndex[0]?.capacityGenerator || {};
    return {};
  }, [dataWithIndex, isConsumer, isGenerator]);

  const getCounterparties = useCallback(
    (match) => {
      return isConsumer ? match.generators : match.consumers;
    },
    [isConsumer],
  );

  // Use useMemo to get the currently selected match/requisition data
  const currentMatch = useMemo(() => {
    if (!selectedSelfReq || !dataWithIndex[0]?.matches) return null;
    return dataWithIndex[0].matches.find(
      (match) => match.selfSlot.id === selectedSelfReq.matchIndex,
    );
  }, [dataWithIndex, selectedSelfReq]);

  // Calculates the effective/current values for a counterparty on the selected requisition
  const getCurrentValues = useCallback(
    (counterparty) => {
      if (!currentMatch || !counterparty) return {};
      const selfSlot = currentMatch.selfSlot || {};
      const matchSlots = counterparty.matchSlots || [];
      const capacityData = getCapacityData();

      // 1. Get defaults based on current match context
      const defaultValues = getDefaultValues(
        user,
        selfSlot,
        matchSlots,
        counterparty,
        capacityData,
      );

      // 2. Get overrides stored for this specific match/counterparty
      const storedValues =
        finalValues?.[selfSlot.id]?.[counterparty.connectId] || {};

      // 3. Get shared payment overrides
      const sharedValues = sharedPaymentValues[counterparty.connectId] || {};

      return {
        ...defaultValues,
        ...storedValues,
        // Override payment fields with shared values, falling back to defaults
        paymentSecurity:
          sharedValues.paymentSecurity ?? defaultValues.paymentSecurity,
        initialMargin:
          sharedValues.initialMargin ?? defaultValues.initialMargin,
        emd: sharedValues.emd ?? defaultValues.emd,
      };
    },
    [currentMatch, finalValues, sharedPaymentValues, getCapacityData, user],
  );

  // --- Event Handlers ---

  const handleInputChange = (e, key, counterparty) => {
    if (!selectedSelfReq || !counterparty) return;

    // Normalize value from event or direct input
    const value =
      e && typeof e === "object" && "target" in e && "value" in e.target
        ? e.target.value
        : e;

    const isPaymentField = ["paymentSecurity", "initialMargin", "emd"].includes(
      key,
    );

    if (isPaymentField) {
      // 1. Update shared state for the counterparty (across all requisitions)
      setSharedPaymentValues((prev) => ({
        ...prev,
        [counterparty.connectId]: {
          ...prev[counterparty.connectId],
          [key]: value,
        },
      }));

      // 2. Also update finalValues for every req that includes this counterparty
      setFinalValues((prev) => {
        const newFinalValues = { ...prev };
        dataWithIndex.forEach((item) => {
          item.matches?.forEach((match) => {
            const selfReqIndex = match.selfSlot.id;
            const counterparties = getCounterparties(match);

            if (
              counterparties.some(
                (cp) => cp.connectId === counterparty.connectId,
              )
            ) {
              newFinalValues[selfReqIndex] = newFinalValues[selfReqIndex] || {};
              newFinalValues[selfReqIndex][counterparty.connectId] = {
                ...(newFinalValues[selfReqIndex][counterparty.connectId] || {}),
                [key]: value,
              };
            }
          });
        });
        return newFinalValues;
      });
    } else {
      // Update specific connection field for the current requisition only
      setFinalValues((prev) => ({
        ...prev,
        [selectedSelfReq.matchIndex]: {
          ...(prev[selectedSelfReq.matchIndex] || {}),
          [counterparty.connectId]: {
            // Merge with existing values from other slots and payment fields
            ...getCurrentValues(counterparty),
            [key]: value,
          },
        },
      }));
    }
  };

  const goToReq = (direction) => {
    if (!selectedSelfReq) return;
    const currentIdx = uniqueSelfReq.findIndex(
      (req) => req.matchIndex === selectedSelfReq.matchIndex,
    );
    const newIdx = currentIdx + direction;
    if (newIdx >= 0 && newIdx < uniqueSelfReq.length) {
      setSelectedSelfReq(uniqueSelfReq[newIdx]);
    }
  };

  const goToPrevReq = () => goToReq(-1);
  const goToNextReq = () => goToReq(1);

  // --- Submission Logic and Validation ---

  const getAllFilledFinalValues = () => {
    const result = {};

    try {
      dataWithIndex.forEach((item) => {
        item.matches?.forEach((match) => {
          const selfReqIndex = String(match.selfSlot.id);
          result[selfReqIndex] = result[selfReqIndex] || {};

          const counterparties = getCounterparties(match);

          counterparties.forEach((counterparty, i) => {
            const connectId = String(counterparty.connectId);
            const matchSlots = counterparty.matchSlots || [];
            const capacityData = getCapacityData();

            // Calculate current/effective values for validation
            const defaultValues = getDefaultValues(
              user,
              match.selfSlot,
              matchSlots,
              counterparty,
              capacityData,
            );
            const storedValues = finalValues?.[selfReqIndex]?.[connectId] || {};
            const sharedValues = sharedPaymentValues[connectId] || {};

            const effectiveValues = {
              ...defaultValues,
              ...storedValues,
              paymentSecurity:
                sharedValues.paymentSecurity ?? defaultValues.paymentSecurity,
              initialMargin:
                sharedValues.initialMargin ?? defaultValues.initialMargin,
              emd: sharedValues.emd ?? defaultValues.emd,
            };

            for (let idx = 0; idx < matchSlots.length; idx++) {
              const reqKey = `req${idx + 1}`;
              const startDate = effectiveValues[`${reqKey}_startDate`];
              const endDate = effectiveValues[`${reqKey}_endDate`];
              const startTime = effectiveValues[`${reqKey}_startTime`];
              const endTime = effectiveValues[`${reqKey}_endTime`];

              // Basic date validation
              if (
                startDate &&
                endDate &&
                new Date(startDate) > new Date(endDate)
              ) {
                alert(
                  `Start Date cannot be greater than End Date in Requisition ${
                    idx + 1
                  }.`,
                );
                throw new Error("INVALID_DATE");
              }
              // Basic time validation (assuming same date logic is handled by date-time check)
              if (startTime && endTime && startTime > endTime) {
                alert(
                  `Start Time cannot be greater than End Time in Requisition ${
                    idx + 1
                  }.`,
                );
                throw new Error("INVALID_TIME");
              }

              // Combined date-time validation
              if (startDate && endDate && startTime && endTime) {
                const startDateTime = new Date(`${startDate}T${startTime}`);
                const endDateTime = new Date(`${endDate}T${endTime}`);

                if (startDateTime >= endDateTime) {
                  alert(
                    `Start Date & Time must be before End Date & Time in Requisition ${
                      idx + 1
                    }.`,
                  );
                  throw new Error("INVALID_DATE_TIME");
                }
              }
            }

            // Prepare final object for submission
            result[selfReqIndex][connectId] = {
              ...effectiveValues,
              id: matchSlots[i]?.id || "", // Assuming id comes from matchSlots, not counterparty
            };
          });
        });
      });
    } catch (err) {
      return null; // Prevent submit
    }

    return result;
  };

  // --- Render Helpers ---

  const renderPaymentFields = (item, currentValues, matchSlotsCount) => {
    const isIntraday = item?.isIntraDay;
    const isDisabled =
      (user === "consumer" && item.initiatorType === "generator") ||
      (user === "generator" && item.initiatorType === "consumer");

    // Simplified list of payment fields to render
    const paymentFields = [
      { key: "initialMargin", label: "Initial Margin", unit: "%" },
      { key: "emd", label: "EMD", unit: " (₹/kWh)" },
      { key: "paymentSecurity", label: "Payment Security", unit: " Days" },
    ];

    return paymentFields.map(({ key, label, unit }) => {
      const isModified = isDisabled && item[key] !== Number(currentValues[key]);
      return (
        <div className={Styles.row} key={key}>
          <div>{label}</div>
          <div style={{ fontWeight: 500 }}>
            {item[key]}
            {unit}
          </div>
          <div>
            <input
              style={{
                borderColor: isModified ? "var(--primary-color-text)" : "",
              }}
              disabled={isDisabled || isIntraday}
              type="number"
              className={Styles.inputBox}
              value={currentValues[key]}
              onChange={(e) => handleInputChange(e, key, item)}
            />
          </div>
        </div>
      );
    });
  };

  const renderSlotFields = (matchSlot, idx, item, currentValues) => {
    const reqKey = `req${idx + 1}`;
    const isDisabled =
      (user === "consumer" && item.initiatorType === "generator") ||
      (user === "generator" && item.initiatorType === "consumer");
    const isIntraday = item?.isIntraDay;

    const rateValue =
      matchSlot?.energyRateStr ||
      matchSlot.sellingCost ||
      matchSlot.landingCost;
    const quantityValue = matchSlot.sellEnergy || matchSlot.purchaseEnergy;

    return (
      <div className={Styles.reqSection} key={matchSlot.id}>
        <div className={Styles.reqHeader}>Requisition {idx + 1}</div>

        {/* Energy Rate */}
        <div className={Styles.row}>
          <div>Energy Rate</div>
          <div style={{ fontWeight: 500 }} className={Styles.greenText}>
            {rateValue} (₹/kWh)
          </div>
          <div>
            <input
              style={{
                borderColor:
                  isDisabled &&
                  rateValue !== Number(currentValues[`${reqKey}_energyRate`])
                    ? "var(--primary-color-text)"
                    : "",
              }}
              disabled={isDisabled}
              type="text"
              className={Styles.inputBox}
              value={currentValues[`${reqKey}_energyRate`]}
              onChange={(e) =>
                handleInputChange(e, `${reqKey}_energyRate`, item)
              }
            />
          </div>
        </div>

        {/* Quantity */}
        <div className={Styles.row}>
          <div>Quantity</div>
          <div style={{ fontWeight: 500 }} className={Styles.greenText}>
            {quantityValue} (MW)
          </div>
          <div>
            <input
              style={{
                borderColor:
                  isDisabled &&
                  quantityValue !== Number(currentValues[`${reqKey}_quantity`])
                    ? "var(--primary-color-text)"
                    : "",
              }}
              disabled={isDisabled}
              type="number"
              className={Styles.inputBox}
              value={currentValues[`${reqKey}_quantity`]}
              onChange={(e) => handleInputChange(e, `${reqKey}_quantity`, item)}
            />
          </div>
        </div>

        {/* Start Date */}
        <div className={Styles.row}>
          <div>Start Date</div>
          <div style={{ fontWeight: 500 }}>
            {formatDate(matchSlot.dateStartAt)}
          </div>
          <div>
            <DateInput
              style={{
                borderColor:
                  isDisabled &&
                  matchSlot.dateStartAt !== currentValues[`${reqKey}_startDate`]
                    ? "var(--primary-color-text)"
                    : "",
              }}
              value={currentValues[`${reqKey}_startDate`] || ""}
              disabled={isDisabled || isIntraday}
              onChange={(e) =>
                handleInputChange(e, `${reqKey}_startDate`, item)
              }
              height={32}
            />
          </div>
        </div>

        {/* End Date */}
        <div className={Styles.row}>
          <div>End Date</div>
          <div style={{ fontWeight: 500 }}>
            {formatDate(matchSlot.dateEndAt)}
          </div>
          <div>
            <DateInput
              value={currentValues[`${reqKey}_endDate`] || ""}
              style={{
                borderColor:
                  isDisabled &&
                  matchSlot.dateEndAt !== currentValues[`${reqKey}_endDate`]
                    ? "var(--primary-color-text)"
                    : "",
              }}
              disabled={isDisabled || isIntraday}
              onChange={(e) => handleInputChange(e, `${reqKey}_endDate`, item)}
              height={32}
            />
          </div>
        </div>

        {/* Start Time */}
        <div className={Styles.row}>
          <div>Start Time</div>
          <div>{formatSlotTime(matchSlot.slotStartAt)}</div>
          <div>
            <TimeInput
              style={{
                borderColor:
                  isDisabled &&
                  String(formatSlotTime(matchSlot.slotStartAt)) !==
                    String(formatSlotTime(currentValues[`${reqKey}_startTime`]))
                    ? "var(--primary-color-text)"
                    : "",
              }}
              noTooltip={true}
              value={formatSlotTime(currentValues[`${reqKey}_startTime`]) || ""}
              disabled={isDisabled}
              onChange={(val) =>
                handleInputChange(
                  { target: { value: val } },
                  `${reqKey}_startTime`,
                  item,
                )
              }
              height={32}
              type="startTime"
            />
          </div>
        </div>

        {/* End Time */}
        <div className={Styles.row}>
          <div>End Time</div>
          <div>{formatSlotTime(matchSlot.slotEndAt)}</div>
          <div>
            <TimeInput
              style={{
                borderColor:
                  isDisabled &&
                  String(formatSlotTime(matchSlot.slotEndAt)) !==
                    String(formatSlotTime(currentValues[`${reqKey}_endTime`]))
                    ? "var(--primary-color-text)"
                    : "",
              }}
              value={formatSlotTime(currentValues[`${reqKey}_endTime`]) || ""}
              disabled={isDisabled}
              onChange={(val) =>
                handleInputChange(
                  { target: { value: val } },
                  `${reqKey}_endTime`,
                  item,
                )
              }
              height={32}
              type="endTime"
              startTime={currentValues[`${reqKey}_startTime`] || ""}
            />
          </div>
        </div>
      </div>
    );
  };

  // --- JSX Rendering ---

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="child-modal-title"
      aria-describedby="child-modal-description"
    >
      <div className={`popupModal ${Styles.modalMainCont}`}>
        <div className="modalHeading">
          <div style={{ color: "var(--fourth-color-text)", fontWeight: 500 }}>
            Edit T&amp;C
          </div>
          <Close
            onClick={handleClose}
            style={{ cursor: "pointer" }}
            alterbutton="false"
          />
        </div>

        {/* Requisition Tabs UI */}
        <div className={Styles.termsMainContainer}>
          <div className={Styles.reqTabsContainer}>
            <button
              className={Styles.arrowBtn}
              onClick={goToPrevReq}
              disabled={
                uniqueSelfReq.findIndex(
                  (req) => req.matchIndex === selectedSelfReq?.matchIndex,
                ) === 0
              }
            >
              &laquo;
            </button>
            {uniqueSelfReq?.map((req) => (
              <button
                key={req.matchIndex}
                className={
                  selectedSelfReq?.matchIndex === req.matchIndex
                    ? Styles.reqTabActive
                    : Styles.reqTab
                }
                onClick={() => setSelectedSelfReq(req)}
              >
                {`Requisition ${req.reqId}`}
              </button>
            ))}
            <button
              className={Styles.arrowBtn}
              onClick={goToNextReq}
              disabled={
                uniqueSelfReq.findIndex(
                  (req) => req.matchIndex === selectedSelfReq?.matchIndex,
                ) ===
                uniqueSelfReq.length - 1
              }
            >
              &raquo;
            </button>
          </div>

          <div className={Styles.tableHeadingCustom}>
            <div>Category</div>
            <div>Counterparty Listing</div>
            <div className={Styles.finalValues}>Final Values</div>
          </div>

          {currentMatch &&
            getCounterparties(currentMatch).map((item) => {
              const matchSlotsCount = item.matchSlots?.length || 1;
              const currentValues = getCurrentValues(item);

              return (
                <div
                  key={item.connectId}
                  className={Styles.tableDetailsMainContCustom}
                >
                  {/* Payment Details Section */}
                  <div className={Styles.sectionBox}>
                    <div className={Styles.row}>
                      <div>Name</div>
                      <div style={{ fontWeight: 600, fontSize: "12px" }}>
                        {item.companyName}
                      </div>
                      <div></div>
                    </div>
                    {renderPaymentFields(item, currentValues, matchSlotsCount)}
                  </div>

                  {/* Slot Details Sections */}
                  {item.matchSlots.map((matchSlot, idx) =>
                    renderSlotFields(matchSlot, idx, item, currentValues),
                  )}
                </div>
              );
            })}
        </div>

        {/* Footer/Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            padding: "15px 25px",
          }}
        >
          <div style={{ display: "flex", gap: "16px" }}>
            <button
              style={{
                padding: "12px 24px",
                border: "none",
                background: "var(--disabled-color-background)",
                color: "#808080ff",
                borderRadius: "6px",
                fontWeight: 500,
                cursor: "pointer",
              }}
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              disabled={termsSubmitLoading}
              style={{
                padding: "12px 24px",
                border: "none",
                background: "var(--primary-color-text)",
                color: "#fff",
                borderRadius: "6px",
                fontWeight: 500,
                cursor: termsSubmitLoading ? "not-allowed" : "pointer",
              }}
              onClick={() => setConformOpen(true)}
            >
              {termsSubmitLoading ? "Submitting..." : "Submit"}
            </button>
            <ConfirmationModal
              open={conformOpen}
              loading={termsSubmitLoading}
              handleClose={() => setConformOpen(false)}
              handleSubmit={() => {
                const filledValues = getAllFilledFinalValues();
                if (filledValues) {
                  handleTermsSubmit(filledValues);
                }
              }}
              header={"Finalize Terms Confirmation"}
              data={`The T&C will be shared with the ${user === "consumer" ? "seller" : "buyer"} for final approval. Do you want to continue?`}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditTermsConnection;
